import { useDelivery } from 'api/hooks/useDelivery';
import { useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router';
import routes, { DELIVERY_ID_PARAM } from 'routes';
import { useCheckInTraysBySet } from './useCheckInTraysBySet';

export const useCheckInRedirect = ({
  deliveryId,
  setKitInstanceId,
  checkInTrayId,
  disabled,
}: {
  deliveryId: string;
  setKitInstanceId: string;
  checkInTrayId: string;
  disabled: boolean;
}) => {
  const { data: delivery } = useDelivery(deliveryId);
  const [checkInTraysBySet] = useCheckInTraysBySet();

  const navigate = useNavigate();

  const deliveryIsCheckedIn = delivery?.status === 'CHECKED_IN';
  const deliverySet = useMemo(
    () => delivery?.sets.find(set => set.kitInstanceId === setKitInstanceId),
    [delivery, setKitInstanceId],
  );
  const deliverySetIsCheckedIn = deliverySet?.inspections?.CHECK_IN?.status == 'CONFIRMED';
  const set = checkInTraysBySet[setKitInstanceId];

  const checkInTray = set?.trays[checkInTrayId];
  const isEditingCheckInTray = Boolean(checkInTray?.trayInstanceId);
  const trayName = checkInTray?.name || '';
  const deliveryTrayIsCheckedIn = useMemo(
    () =>
      delivery?.sets
        .find(set => set.kitInstanceId === setKitInstanceId)
        ?.trays.find(
          tray =>
            // we need to use the tray name because locally we may not necessarily have the trayInstanceId as it might not be created yet
            tray.name === trayName,
        )
        ?.inspections.some(inspection => inspection.stage === 'CHECK_IN' && inspection.status === 'CONFIRMED'),
    [delivery, setKitInstanceId, trayName],
  );

  // if delivery, set or tray is already checked in, e.g. if the user clicks back after checking it in, then redirect to the delivery info page
  useEffect(() => {
    // we still want to display the delivery checked in dialog after the tray is checked in
    if (disabled) {
      return;
    }

    // if the user is editing a check in tray, don't redirect
    if (isEditingCheckInTray) {
      return;
    }

    //if the delivery/set/last tray is checked in, navigate to the check-out page
    if (deliveryIsCheckedIn || deliverySetIsCheckedIn) {
      navigate(routes.CheckOut.path.replace(DELIVERY_ID_PARAM, deliveryId));

      return;
    }

    // otherwise navigate back to the check in workflow
    if (deliveryTrayIsCheckedIn) {
      navigate(routes.CheckInWorkflow.path.replace(DELIVERY_ID_PARAM, deliveryId));

      return;
    }
  }, [
    deliveryId,
    deliveryIsCheckedIn,
    deliverySetIsCheckedIn,
    deliveryTrayIsCheckedIn,
    disabled,
    isEditingCheckInTray,
    navigate,
  ]);
};
